import { LitElement, html, css, unsafeCSS, nothing } from "lit";
import type { TemplateResult } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import "./web-kiosk-countdown-box";
import type { WhModalElement } from "@wh-web-front-end/wh-component-library/dist/elements/wh-modal";

import type { InactivityModalContent } from "../types/content";

import type { WebKioskCountdownBoxElement } from "./web-kiosk-countdown-box";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-button";

import "@wh-web-front-end/wh-component-library/dist/elements/wh-modal";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-timeout-modal": WebKioskTimeoutModalElement;
  }
}

@customElement("web-kiosk-timeout-modal")
export class WebKioskTimeoutModalElement extends LitElement {
  private readonly contentEntryKey = "inactivityModal";

  @property() content: InactivityModalContent | undefined = undefined;

  @property({ type: Number }) timeoutNumber = 30;

  @property({ type: Boolean, reflect: true }) open = false;

  @query("wh-modal") modal: WhModalElement | undefined;

  @query("web-kiosk-countdown-box") countdownBox:
    | WebKioskCountdownBoxElement
    | undefined;

  render(): TemplateResult | typeof nothing {
    if (!this.content) {
      return nothing;
    }

    return html`
      <wh-modal flat fitContent .open=${this.open}>
        <label id="close" slot="close-button-content">
          <img src="assets/shared/close.svg" alt=${this.content.closeBtnText} />
          ${this.content.closeBtnText}
        </label>
        <div class="modal-body">
          <web-kiosk-countdown-box
            .countdownNumber=${this.timeoutNumber}
          ></web-kiosk-countdown-box>
          <h3 id="modal-title">${this.content.title}</h3>
          <h4 id="modal-description">${this.content.description}</h4>
          <p>
            ${`${this.content.timeoutMessageStart} ${this.timeoutNumber} ${this.content.timeoutMessageFinish}`}
          </p>
          <div class="buttons">
            <wh-button
              variant="primary"
              pill
              size="medium"
              id="stay-button"
              fullwidth="true"
              >${this.content.stayBtnText}
            </wh-button>
            <wh-button
              variant="secondary"
              id="leave-button"
              fullwidth="true"
              @click=${() => {
                this.dispatchEvent(
                  new CustomEvent("leave", {
                    bubbles: true,
                    composed: true,
                  })
                );
              }}
              >${this.content.leaveBtnText}
            </wh-button>
          </div>
        </div>
      </wh-modal>
    `;
  }

  static styles = css`
    :host {
      --web-kiosk-p-color: #0a1414;
      --web-kiosk-text-color: #323264;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-button-text-color: #fff;
      --wh-modal-background-color: #f5f5f0;
      --web-kiosk-dimension-spacer-xxs: 8px;
      --web-kiosk-dimension-spacer-xs: 16px;
      --web-kiosk-dimension-spacer-m: 32px;
      --web-kiosk-dimension-spacer-xl: 64px;
      --web-kiosk-font-size-s: 24px;
      --web-kiosk-font-size-m: 32px;
      --web-kiosk-font-size-l: 48px;
    }

    .modal-body {
      overflow-y: auto;
      border-bottom-right-radius: var(--web-kiosk-dimension-spacer-xs, 16px);
      border-bottom-left-radius: var(--web-kiosk-dimension-spacer-xs, 16px);
      padding: 156px 40px;
      /* stylelint-disable-next-line declaration-colon-space-after */
      background: ${unsafeCSS(`
        url("assets/timeout-modal/waving.svg") no-repeat right bottom,
        url("assets/timeout-modal/ribbon.svg") bottom no-repeat,
        var(--wh-modal-background-color, hotpink);
      `)};
      background-size: 20%, contain;
    }

    #close {
      display: flex;
      align-items: center;
      color: var(--web-kiosk-link-color, hotpink);
      background: none;
      border: none;
      /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
      font-family: "Source Sans Pro";
      font-size: var(--web-kiosk-font-size-m, 32px);
      font-weight: 700;
      line-height: 40px;
      margin-left: auto;
    }

    #close:hover {
      cursor: pointer;
    }

    #close img {
      margin-right: var(--web-kiosk-dimension-spacer-xs, 8px);
      height: 24px;
      width: 24px;
    }

    p {
      color: var(--web-kiosk-p-color, hotpink);
      font-size: var(--web-kiosk-font-size-m, 32px);
      font-weight: 400;
      line-height: 52px;
    }

    #modal-description {
      color: var(--web-kiosk-p-color, hotpink);
      margin-top: var(--web-kiosk-dimension-spacer-xs, 16px);
      margin-bottom: var(--web-kiosk-dimension-spacer-xs, 16px);
      font-size: var(--web-kiosk-font-size-m, 32px);
      line-height: 38px;
    }

    wh-modal {
      text-align: center;
      display: flex;
      flex-direction: column;
      justify-items: center;

      --wh-modal-background-color: #f5f5f0;
      --wh-modal-border-radius: 30px;
    }

    #modal-title {
      color: var(--web-kiosk-text-color, hotpink);
      font-size: var(--web-kiosk-font-size-l, 48px);
      line-height: 62px;
    }

    wh-button {
      margin-right: 44px;
      border-radius: 50px;
      font-weight: 600;
      font-size: var(--web-kiosk-font-size-s, 24px);
      line-height: 20px;
      border: 2px solid var(--web-kiosk-text-color, hotpink);
      width: 300px;
    }

    #stay-button {
      color: var(--web-kiosk-button-text-color, hotpink);
      background-color: var(--web-kiosk-text-color, hotpink);
    }

    #leave-button {
      color: var(--web-kiosk-text-color, hotpink);

      --wh-theme-colors-secondary: lightgray;
      --wh-theme-colors-secondary-weak: lightgray;
    }

    .buttons {
      display: flex;
      justify-content: center;
      margin-top: 24px;
    }
  `;
}
